<script setup>
import { ref, inject, computed } from 'vue';
import { useMemberAssessmentStore } from '@/stores/memberAssessment';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import { useToast } from 'vue-toastification';
import { trans } from 'laravel-vue-i18n';
import { router } from '@inertiajs/vue3';

const { search, submit, destroy } = useMemberAssessmentStore();
const { activeAssessments } = storeToRefs(useMemberAssessmentStore());

const params = ref({
    term: '',
    page: 1,
    sort_by: null,
    per_page: 10,
    status: null,
});

const searchMemberAssessments = () => {
    search({
        ...params.value,
    });
};

const tableUpdate = ({ page, sortBy, itemsPerPage }) => {
    Object.assign(params.value, { page, sort_by: sortBy, per_page: itemsPerPage });
    searchMemberAssessments();
};

const assessmentName = (item) => item.assessment?.name || item.domain?.name || '-';
const domainName = (item) => item.domain?.name || item.assessment?.domain?.name || '-';

const modals = inject('modals');

const toast = useToast();

const submitAssessment = async (assessment) => {
    if (assessment.status.name === 'Submitted') {
        toast.error('This assessment has already been submitted.');
        return;
    } else if (assessment.completion_percentage < 100) {
        toast.error('Please complete all questions before submitting the assessment.');
        return;
    }
    try {
        const response = await submit({ hash: assessment.hash });

        toast.success('Assessment submitted successfully');

        if (response?.generation_started) {
            toast.info('Profile generation started..');
        }
    } catch (error) {
        toast.error('Failed to submit assessment');
    }    
    
    searchMemberAssessments();
};

const remove = async (memberAssessment) => {
    const confirm = await modals.show('ConfirmationModal', {
        title: trans('label.confirm_delete'),
        prompt: trans('label.are_you_sure_delete'),
    });
    if (confirm) {
        await destroy({ hash: memberAssessment.hash });
        searchMemberAssessments();
    }
};

const { profile } = storeToRefs(useUserStore());
const isAccessingMember = computed(() => !!profile.value?.is_accessing_member);

const hasAssessments = computed(() => {
    return !!activeAssessments.value?.data?.length;
});

const completedQuestions = (item) => {
    const total = item.questions_count || 0;

    if (!total) {
        return 0;
    }

    return Math.round((item.completion_percentage / 100) * total);
};

const statusChipClasses = (statusName) => {
    switch (statusName) {
    case 'In Progress':
        return 'text-primary bg-primary/10';

    case 'Completed':
    case 'Submitted':
        return 'text-green-700 bg-green-100';

    case 'Not Started':
    default:
        return 'text-gray-600 bg-secondary/10'; 
    }
};

const primaryButtonLabel = (statusName) => {
    if (statusName === 'Submitted' || statusName === 'Completed') {
        return trans('label.assessment_review_results');
    }

    if (statusName === 'Not Started') {
        return trans('label.assessment_begin');
    }

    return trans('label.assessment_continue');
};

const openAssessment = (item) => {
    router.visit('/member-assessments/' + item.hash + '/flow');
};

const tableActions = computed(() => ([
    {
        title: primaryButtonLabel('Submitted'),
        icon: 'mdi-login',
        isVisible: (item) => item.status.name === 'Submitted' || item.status.name === 'Completed',
        action: (item) => openAssessment(item),
    },
    {
        title: primaryButtonLabel('Not Started'),
        icon: 'mdi-login',
        isVisible: (item) => item.status.name === 'Not Started',
        action: (item) => openAssessment(item),
    },
    {
        title: primaryButtonLabel('In Progress'),
        icon: 'mdi-login',
        isVisible: (item) =>
            item.status.name !== 'Submitted' &&
            item.status.name !== 'Completed' &&
            item.status.name !== 'Not Started',
        action: (item) => openAssessment(item),
    },
    {
        title: trans('label.submit'),
        icon: 'mdi-send',
        isVisible: (item) => item.status.name !== 'Submitted',
        action: (item) => submitAssessment(item),
    },
    ...(isAccessingMember.value ? [{
        title: trans('label.delete'),
        icon: 'mdi-delete',
        action: (item) => remove(item),
    }] : []),
]));

</script>

<template>
<MainContentLayout>
    <template #breadcrumb><BreadCrumb /></template>
    <template #title>{{ $t('label.management_title', { what: 'Assessments' }) }}</template>
    <!-- Desktop View Only -->
    <div class="hidden md:block">
        <DataTable
            v-if="hasAssessments"
            :data="activeAssessments"
            :headers="[
                { title: $t('label.domain'), key: 'domain', value: item => domainName(item), sortable: false },
                { title: $t('label.assessment'), key: 'assessment', value: item => assessmentName(item), sortable: false },
                { title: $t('label.status'), key: 'status', value: item => item.status.name, sortable: false },
                { title: $t('label.sport'), key: 'sport', value: item => item.member_profile.sport.name, sortable: false },
                { title: $t('label.completion_percentage'), key: 'completion_percentage', value: item => item.completion_percentage, sortable: false },
            ]"
            :actions="tableActions"
            @table-update="tableUpdate" />
        <div v-else class="text-center py-12 text-gray-500">
            {{ $t('label.profile_must_exist_before_accessing_assessments') }}
        </div>
    </div>

    <!-- Mobile View Only -->
    <div class="md:hidden">
        <div
            v-if="hasAssessments"
            class="max-w-md mx-auto px-4 pb-8 space-y-4">

            <div
                v-for="assessment in activeAssessments.data"
                :key="assessment.hash"
                class="bg-white rounded-2xl shadow-md shadow-neutral-300/20 p-5 space-y-3">

                <p class="text-sm font-semibold text-primary">
                    {{ domainName(assessment) }}
                </p>

                <div class="flex items-start justify-between">
                    <h2 class="text-lg font-semibold text-gray-900 leading-tight">
                        {{ assessmentName(assessment) }}
                    </h2>

                    <span
                        class="px-3 py-1 text-xs font-semibold rounded-full"
                        :class="statusChipClasses(assessment.status.name)">
                        {{ assessment.status.name }}
                    </span>
                </div>

                <p
                    v-if="assessment.assessment?.description"
                    class="text-sm text-gray-600 leading-snug">
                    {{ assessment.assessment.description }}
                </p>

                <p class="text-sm text-gray-500">
                    {{ $t('label.sport') }}:
                    {{ assessment.member_profile?.sport?.name }}
                </p>

                <div class="flex items-center text-sm text-gray-700">
                    <i class="mdi mdi-check mr-1 text-base" />

                    <span v-if="assessment.questions_count">
                        {{ $t('label.questions_completed', {
                            completed: completedQuestions(assessment),
                            total: assessment.questions_count,
                        }) }}
                    </span>

                    <span v-else>
                        {{ $t('label.percent_complete_text', { value: assessment.completion_percentage }) }}
                    </span>
                </div>

                <div class="relative w-full h-3 rounded-full bg-gray-200 overflow-hidden">
                    <div class="absolute inset-0 bg-primary/10" />
                    <div 
                        v-if="assessment.completion_percentage > 0"
                        class="absolute top-0 left-0 h-full bg-primary transition-all duration-500 ease-out"
                        :style="{ width: `${assessment.completion_percentage}%` }" />
                </div>

                <StyledButton
                    type="button"
                    class="w-full py-3 rounded-xl bg-primary text-white text-sm font-semibold tracking-wide shadow-sm"
                    @click="openAssessment(assessment)">
                    {{ primaryButtonLabel(assessment.status.name) }}
                </StyledButton>
            </div>
        </div>

        <div v-else class="text-center py-12 text-gray-500 px-4">
            {{ $t('label.profile_must_exist_before_accessing_assessments') }}
        </div>
    </div>
</MainContentLayout>
</template>
